/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { User } from '@src/app/model/user';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { MessageService, MsgDef } from '@src/app/shared/message.service';
import { NavigationService } from '@src/app/shared/navigation/navigation.service';
import { concatMap, map } from 'rxjs/operators';
import { TenantService } from '@src/app/shared/tenant.service';
import { TenantId } from '@src/app/model/abstract-object';
import { isObservable, Observable, of } from 'rxjs';
import { unknownUserRoute } from '@src/app/modules/user/join-an-omc/join-an-omc.component';

type GuardReturn = boolean | UrlTree | Observable<boolean | UrlTree>;

@Injectable({
    providedIn: 'root',
})

export class LoggedInGuard {
    private router = inject(Router);
    private currentUserService = inject(CurrentUserService);
    private tenantSvc = inject(TenantService);
    private nav = inject(NavigationService);
    private msg = inject(MessageService);
    private cds = inject(ConfirmDialogService);


    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) : GuardReturn {
        if (!this.currentUserService.gotUser) {
            return this.currentUserService.getCurrentUser().pipe(concatMap(() => {
                const ret =  this.tenantActivate(route, state);
                if (isObservable(ret)) {
                    return ret;
                } else {
                    return of(ret);
                }
            }))
        } else {
            return this.tenantActivate(route, state);
        }
    }

    tenantActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot)  : GuardReturn {
        
        const targetTenantId: TenantId = route.params['tenantId'];
        const currentTenantId: TenantId = this.tenantSvc.getCurrentTenantId();

        if (targetTenantId === currentTenantId) {
            return this.userCanActivateInTenant(route, state);
        }
        return this.currentUserService.switchTenant(targetTenantId).pipe( map( (cu => {
            if (cu) {
                return this.userCanActivateInTenant(route, state);
            } else {
                console.warn('Switch failed, go home');
                return this.goHome();
            }
        })));
    }

    goHome() {
        return this.router.createUrlTree(['/']);
    }

    whoAreYou() {
        return this.router.createUrlTree([unknownUserRoute]);
    }

    loginPlease(state: RouterStateSnapshot) {
        const queryParams = { returnUrl: state.url };
        return this.router.createUrlTree(['/login'], { queryParams });        
    }

    private userCanActivateInTenant(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) : boolean | UrlTree {

        if (!this.currentUserService.gotUser || !this.currentUserService.currentUser) {
            return this.loginPlease(state);
        }

        const user: User = this.currentUserService.currentUser;
        if (user.currentTeam === null) {
            this.msg.show(new MsgDef($localize`You are not associated to an OMC, you could request to register with one`, 'warn'));
            return this.whoAreYou();
        }

        let allowed = false;

        const actualPath = route.routeConfig.path;
        const path = actualPath.replace(':tenantId/', '');

        /* Path only ends /NEW if we have special NEW component with formConfig.forceNew, otherwise comes in as /:itemId where itemId = NEW*/
        const basePath = path.endsWith('/NEW') ? path.replace('/NEW', '') : path.replace('/:itemId', '');         

        const nav = this.nav.getItem(basePath);
        const forceTeam = route.params._forceTeam;
        const privilege = User.getPathPrivilege(user, basePath, forceTeam);
        if (actualPath === ':tenantId/home') {
            allowed = true; // Everyone can see the home page, but we do want the guard to force the tenant to be correct!
        } else if (path === basePath && (privilege.getIndex || privilege.post && nav?.showToPoster)) {
            allowed = true;
        } else if (path.includes(':itemId') && privilege.get) {
            allowed = true;
        } else if (path.endsWith('/NEW') && privilege.post) {
            allowed = true;
        } else {
            const teamName = User.getActiveTeamName(user, forceTeam);
            const title = $localize`${teamName} has not given you access to '/${basePath}`;

            const msg = $localize`${user.name} does not have privileges to access '/${basePath}' for ${teamName}.
            It may be that you need to login as a different user or switch into a different team to access '/${basePath}'
            `;
            this.cds.alert(title, msg);
        }

        if (!allowed) {
            console.log('NO privileges - GO Home', {basePath, actualPath});
            return this.goHome();
        }
        const tenantId: string = route.params['tenantId'];
        if (tenantId) {
            this.tenantSvc.setCurrentTenantId(tenantId);
        }

        return allowed;
    }
}
